<template>
    <modal
        id="employee-day-shift-report-modal"
        v-model="showModal"
        :title="$t('day_shift_report')"
        :scrollable="false"
        :preloader="loading"
        @submit="closeModal"
        submit-btn-class="d-none"
        cancel-btn-class="d-none"
    >
        <div class="p-3">
            <div class="d-flex justify-content-center mb-4">
                <div class="btn-group btn-group-toggle" data-toggle="buttons">
                    <label class="btn btn-outline-primary cursor-pointer" :class="{ active: chartType === 'count' }">
                        <input type="radio" name="options" id="option1" autocomplete="off" :checked="chartType === 'count'" @change="chartType = 'count'"> {{ $t('number_of_people') }}
                    </label>
                    <label class="btn btn-outline-primary cursor-pointer" :class="{ active: chartType === 'hours' }">
                        <input type="radio" name="options" id="option2" autocomplete="off" :checked="chartType === 'hours'" @change="chartType = 'hours'"> {{ $t('total_hours') }}
                    </label>
                </div>
            </div>

            <div v-if="hasData" class="mb-4">
                <div class="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2" v-for="(item, index) in legendData" :key="index">
                    <div class="d-flex align-items-center">
                        <span class="d-inline-block mr-2" :style="{ width: '12px', height: '12px', backgroundColor: item.color, borderRadius: '2px' }"></span>
                        <span>{{ item.name }}</span>
                    </div>
                    <div>
                        {{ item.value }}
                    </div>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2 border-bottom pb-2">
                    <div class="d-flex align-items-center">
                        <span class="d-inline-block mr-2" :style="{ width: '12px' }"></span>
                        <span class="font-weight-bold">{{ $t('total') }}</span>
                    </div>
                    <div class="font-weight-bold">
                        {{ totalValue }}
                    </div>
                </div>
            </div>

            <div class="position-relative" style="min-height: 320px;">
                <app-chart
                    v-if="hasData && loaded"
                    type="dough-chart"
                    :height="320"
                    :labels="labels"
                    :data-sets="dataSets"
                    :show-legend="false"
                    :key="chartType"
                />
                <app-empty-data-block v-if="!hasData" :message="$t('nothing_to_show_here')"/>
            </div>
        </div>
    </modal>
</template>

<script>
import Modal from "../../../../../common/Components/Helper/Modal";
import HelperMixin from "../../../../../common/Mixin/Global/HelperMixin";
import { numberFormatter } from "../../../../../common/Helper/Support/SettingsHelper";
import AppChart from "../../../../../core/components/charts/Index";

export default {
    name: "EmployeeDayShiftReportModal",
    components: { Modal, AppChart },
    mixins: [HelperMixin],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        attendances: {
            type: Object,
            required: true
        },
        allDayShifts: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            chartType: 'count', // 'count' or 'hours'
            loaded: false,
        }
    },
    mounted() {
        setTimeout(() => {
            this.loaded = true;
        }, 500);
    },
    computed: {
        showModal: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        aggregatedData() {
            const data = {};
            
            // Initialize with all day shifts
            this.allDayShifts.forEach(shift => {
                data[shift.id] = {
                    name: shift.name,
                    color: shift.color,
                    count: 0,
                    hours: 0
                };
            });

            const employees = this.attendances.data || [];

            employees.forEach(employee => {
                if (employee.day_shifts) {
                    employee.day_shifts.forEach(shift => {
                        const id = shift.id;
                        if (!data[id]) {
                            // Should theoretically not happen if allDayShifts contains everything, 
                            // but good for safety if a shift was deleted but still in history
                            data[id] = {
                                name: shift.name,
                                color: shift.color,
                                count: 0,
                                hours: 0
                            };
                        }
                        data[id].count++;
                        
                        // Calculate hours
                        if (shift.start_at && shift.end_at) {
                            const start = this.parseTime(shift.start_at);
                            const end = this.parseTime(shift.end_at);
                            let duration = end - start;
                            if (duration < 0) duration += 24 * 60; // Handle overnight shifts if necessary
                            data[id].hours += duration / 60;
                        }
                    });
                }
            });
            return Object.values(data);
        },
        labels() {
            return this.aggregatedData.map(item => item.name);
        },
        dataSets() {
            const data = this.aggregatedData.map(item => 
                this.chartType === 'count' ? item.count : Number(item.hours.toFixed(2))
            );
            const backgroundColor = this.aggregatedData.map(item => item.color);

            return [{
                borderWidth: 0,
                backgroundColor: backgroundColor,
                data: data
            }];
        },
        legendData() {
            return this.aggregatedData.map(item => ({
                name: item.name,
                color: item.color,
                value: this.chartType === 'count' 
                    ? `${item.count} ${this.$t('people')}`
                    : `${numberFormatter(item.hours)} ${this.$t('hours')}`
            }));
        },
        hasData() {
            return this.aggregatedData.length > 0;
        },
        totalValue() {
            const total = this.aggregatedData.reduce((sum, item) => {
                return sum + (this.chartType === 'count' ? item.count : item.hours);
            }, 0);
            
            return this.chartType === 'count' 
                ? `${total} ${this.$t('people')}`
                : `${numberFormatter(total)} ${this.$t('hours')}`;
        }
    },
    methods: {
        closeModal() {
            this.showModal = false;
        },
        parseTime(timeStr) {
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours * 60 + minutes;
        }
    }
}
</script>
